// What an entity's displayed NAME is allowed to be.
//
// Entity names used to be fixed strings baked into a spawn template ("The Defender"), so nothing
// ever had to validate one. They are now also PLAYER INPUT: a camp member can be named by whoever
// recruited it (see the ember role picker / the "Rename" order), and in a shared camp that name is
// shown to every other player in the roster, so it goes through here on the way in.
//
// Lives in common so the one cap and the one cleaner are shared: the client limits what can be
// typed, the server refuses to store anything else, and neither can drift from the other.

// Longest name we keep. Matched to the ember roster card's own display cap (NAME_MAX_CHARS in
// ember_roster_card.ts), which is where names are read most: a longer one would arrive and then be
// shown ellipsized, which looks like the field lost the end of what was typed.
export const ENTITY_NAME_MAX_CHARS = 11

/**
 * Whether a code point may appear in a name: everything except the C0/C1 control codes and the
 * zero-width / directional-formatting marks. That second group is the one worth being explicit
 * about -- those spell nothing, they let a name hide characters inside itself or reorder the row it
 * is drawn in, which is a cheap way to misrepresent one player's crew to another.
 *
 * Written as comparisons rather than a regex character class on purpose: a class of escapes for
 * these is unreadable, and one mistyped digit silently widens it over real letters.
 */
const isNameCharacter = (codePoint: number): boolean => {
    if (codePoint <= 0x1f) {
        return false // C0 controls (tab and newline included -- whitespace is collapsed below)
    }
    if (codePoint >= 0x7f && codePoint <= 0x9f) {
        return false // delete + C1 controls
    }
    if (codePoint >= 0x200b && codePoint <= 0x200f) {
        return false // zero-width space/joiners, LTR/RTL marks
    }
    if (codePoint === 0x2028 || codePoint === 0x2029) {
        return false // line + paragraph separators
    }
    if (codePoint >= 0x202a && codePoint <= 0x202e) {
        return false // bidi embeddings and overrides
    }
    if (codePoint >= 0x2066 && codePoint <= 0x2069) {
        return false // bidi isolates
    }
    if (codePoint === 0xfeff) {
        return false // byte-order mark / zero-width no-break space
    }
    return true
}

/**
 * Clean a proposed entity name, returning "" for anything we won't store.
 *
 * The cap is applied AFTER collapsing whitespace so padding can't be used to push real characters
 * past it, and trimmed again afterwards so a cut that lands on a space doesn't leave one dangling.
 */
export const sanitizeEntityName = (raw: unknown): string => {
    if (typeof raw !== "string") {
        return ""
    }
    // Array.from splits by code POINT, so an astral character (an emoji) is weighed, kept or cut as
    // one thing rather than as two halves of a surrogate pair.
    const printable = Array.from(raw)
        .filter(character => isNameCharacter(character.codePointAt(0) || 0))
        .join("")
    return printable
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, ENTITY_NAME_MAX_CHARS)
        .trim()
}
